import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import {
  CalendarDays,
  Clock,
  Syringe,
  IndianRupee,
  Bell,
  Download,
  Upload,
  Camera,
} from "lucide-react";
import Navbar from "../../components/public/Navbar";
import config from "../../config";
import { fieldError, indianMobile10 } from "../../lib/validation";
import { normalizePincode, pinServiceError } from "../../lib/servicePincodes";
import {
  authHeaders,
  readUser,
  formatDisplayName,
  formatDisplayEmail,
  requireCustomerAuth,
  notifyAuthChanged,
  CustomerShell,
} from "./customerAccount";
import { BookingSlotCalendar, localIsoDate, addDaysIso } from "./BookingWizard";
import { openAuthedFile, paymentSucceeded } from "../staff/staffShared";
import IceboxMap from "../../components/IceboxMap";
import IceboxTempBar from "../../components/IceboxTempBar";
import DmyDateInput from "../../components/DmyDateInput";
import { openVaccineReport } from "../../lib/vaccineReport";
import { formatDateDMY, formatDateTimeDMY, ageFromDob, FAMILY_RELATIONS } from "../../lib/dates";
export { BookingWizard } from "./BookingCheckout";
export { CustomerShell, authHeaders, readUser, formatDisplayEmail, requireCustomerAuth } from "./customerAccount";

const STATUS_STYLE = {
  awaiting_payment: "bg-amber-100 text-amber-800",
  pending_confirmation: "bg-[#f37721]/15 text-[#f37721]",
  confirmed: "bg-emerald-100 text-emerald-800",
  assigned: "bg-[#673c96]/15 text-[#673c96]",
  on_the_way: "bg-sky-100 text-sky-800",
  arrived: "bg-sky-100 text-sky-800",
  completed: "bg-slate-100 text-slate-700",
  cancelled: "bg-red-100 text-red-700",
};

export function OnboardingPage() {
  const navigate = useNavigate();
  const existing = readUser();
  const [full_name, setName] = useState(existing.full_name || "");
  const [date_of_birth, setDob] = useState("");
  const [address, setAddress] = useState("");
  const [phone, setPhone] = useState(existing.phone && !String(existing.phone).includes("@") ? String(existing.phone).slice(-10) : "");
  const [error, setError] = useState("");

  useEffect(() => { requireCustomerAuth(navigate); }, [navigate]);

  const submit = async (e) => {
    e.preventDefault();
    const err = fieldError({ name: full_name, phone: phone || undefined });
    if (err) { setError(err); return; }
    try {
      const { data } = await axios.post(
        `${config.API}/customer/onboarding`,
        { full_name, date_of_birth, address, phone: phone ? indianMobile10(phone) : "" },
        { headers: authHeaders() }
      );
      if (data.user) {
        localStorage.setItem("customer_user", JSON.stringify(data.user));
        notifyAuthChanged();
      }
      navigate("/dashboard");
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to save profile");
    }
  };

  return (
    <div className="min-h-screen bg-[#f4f5f7]">
      <Navbar accountMode />
      <div className="pt-[72px] flex items-center justify-center px-4 py-16">
        <form onSubmit={submit} className="w-full max-w-md bg-white rounded-2xl p-8 shadow-sm border border-[#673c96]/8 space-y-4">
          <h1 className="text-2xl font-bold text-[#311b52]" style={{ fontFamily: "Poppins" }}>Complete your profile</h1>
          <p className="text-sm text-[#6b7280]" style={{ fontFamily: "Montserrat" }}>One shared account for web and future mobile apps.</p>
          <input required value={full_name} onChange={(e) => setName(e.target.value)} placeholder="Full name" className="w-full rounded-xl border border-[#e5e7eb] px-4 py-3" data-testid="onboard-name" />
          <input value={phone} onChange={(e) => setPhone(e.target.value.replace(/\D/g, '').slice(0, 10))} maxLength={10} placeholder="Mobile (10 digits)" className="w-full rounded-xl border border-[#e5e7eb] px-4 py-3" />
          <div>
            <p className="text-xs text-[#6b7280] mb-1">Date of birth *</p>
            <DmyDateInput required noFuture value={date_of_birth} onChange={setDob} className="w-full rounded-xl border border-[#e5e7eb] px-4 py-3" />
            {ageFromDob(date_of_birth) !== "" && <p className="text-[11px] text-[#6b7280] mt-1">Age: {ageFromDob(date_of_birth)} years</p>}
          </div>
          <input value={address} onChange={(e) => setAddress(e.target.value)} placeholder="Home address (optional)" className="w-full rounded-xl border border-[#e5e7eb] px-4 py-3" />
          <button className="w-full bg-[#f37721] text-white rounded-full py-3 font-semibold" style={{ fontFamily: "Montserrat" }}>Continue to dashboard</button>
          {error && <p className="text-sm text-[#ee4036]">{error}</p>}
        </form>
      </div>
    </div>
  );
}

function StatCard({ label, value, icon: Icon, iconBg, iconColor }) {
  return (
    <div className="bg-white rounded-2xl p-5 shadow-sm border border-black/[0.03] flex items-center gap-4">
      <div className={`w-12 h-12 rounded-xl flex items-center justify-center shrink-0 ${iconBg}`}>
        <Icon className={`w-6 h-6 ${iconColor}`} />
      </div>
      <div>
        <p className="text-sm text-[#6b7280]" style={{ fontFamily: "Montserrat, sans-serif" }}>{label}</p>
        <p className="text-2xl font-bold text-[#1a1a2e] mt-0.5" style={{ fontFamily: "Poppins, sans-serif" }}>{value}</p>
      </div>
    </div>
  );
}

function hoursUntilAppointment(dateStr, slotStr) {
  if (!dateStr) return 0;
  const hour = parseInt(String(slotStr || "09:00").split(":")[0], 10) || 9;
  const minute = parseInt(String(slotStr || "09:00").split(":")[1] || "0", 10) || 0;
  const dt = new Date(`${dateStr}T${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}:00`);
  return (dt.getTime() - Date.now()) / 36e5;
}

function VaccineTrackingPanel({ bookingId }) {
  const [track, setTrack] = useState(null);
  useEffect(() => {
    if (!bookingId) return undefined;
    let alive = true;
    const load = () => {
      axios.get(`${config.API}/customer/bookings/${bookingId}/tracking`, { headers: authHeaders() })
        .then(({ data }) => { if (alive) setTrack(data); })
        .catch(() => {});
    };
    load();
    const t = setInterval(load, 8000);
    return () => { alive = false; clearInterval(t); };
  }, [bookingId]);
  if (!track) return null;
  const cc = track.cold_chain || {};
  const live = cc.live;
  return (
    <div className="border-t pt-4 space-y-2" data-testid="vaccine-tracking-panel">
      <p className="text-sm font-semibold text-[#311b52]">Your vaccine</p>
      <p className="text-sm text-[#311b52]">{track.vaccine_name}</p>
      <p className="text-xs text-[#6b7280]">{track.message}</p>
      {cc.available && (
        <IceboxTempBar temp={cc.temp} lastUpdated={cc.last_updated} explain />
      )}
      {cc.lid_open && <p className="text-xs text-amber-700">Icebox lid was opened</p>}
      {cc.reconnecting && (
        <p className="text-xs text-amber-800 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2" data-testid="icebox-reconnecting">
          Reconnecting to the icebox. Last temperature remains visible. This is a brief signal pause, not a cold-chain failure by itself.
        </p>
      )}
      {live?.lat != null && (
        <div>
          <p className="text-xs font-semibold text-[#673c96] mb-1">Nurse is on the way (approximate)</p>
          <IceboxMap lat={live.lat} lng={live.lng} label="Nurse icebox" height={200} />
        </div>
      )}
    </div>
  );
}

function BookingDetailDrawer({ selected, reschedule, setReschedule, onClose, onCancel, onReschedule, onBookingPatch, rescheduleWindowHours = 24 }) {
  const [calDays, setCalDays] = useState([]);
  const [calTotal, setCalTotal] = useState(0);
  const [calLoading, setCalLoading] = useState(false);
  const [hours, setHours] = useState([]);
  const [hoursLoading, setHoursLoading] = useState(false);
  const [rxBusy, setRxBusy] = useState(false);
  const [rxMsg, setRxMsg] = useState("");

  useEffect(() => {
    if (!selected) return;
    const start = localIsoDate();
    const end = addDaysIso(start, 45);
    setCalLoading(true);
    axios.get(`${config.API}/availability/calendar`, { params: { start, end } })
      .then(({ data }) => {
        const days = data.days || [];
        setCalDays(days);
        setCalTotal(Number(data.total_slots) || days.reduce((n, d) => n + (d.available_slots || 0), 0));
      })
      .catch(() => { setCalDays([]); setCalTotal(0); })
      .finally(() => setCalLoading(false));
  }, [selected?.id]);

  useEffect(() => {
    if (!reschedule.date) { setHours([]); return; }
    setHoursLoading(true);
    axios.get(`${config.API}/availability/slots`, { params: { date: reschedule.date } })
      .then(({ data }) => {
        const today = localIsoDate();
        let next = data.slots || [];
        if (reschedule.date === today) {
          const nowHour = new Date().getHours();
          next = next.filter((s) => parseInt(String(s.hour || "0").split(":")[0], 10) > nowHour);
        }
        setHours(next.filter((s) => s.status === "available"));
      })
      .catch(() => setHours([]))
      .finally(() => setHoursLoading(false));
  }, [reschedule.date]);

  if (!selected) return null;
  const hasRx = Boolean(selected.prescription_file_reference || selected.patient_prescription_file_reference);
  const canAttach = !hasRx && !["cancelled", "completed"].includes(selected.status);
  const slotLocked = selected.status !== "pending_confirmation" || paymentSucceeded(selected.payment_status);
  const windowLocked = hoursUntilAppointment(selected.preferred_date, selected.preferred_slot) <= Number(rescheduleWindowHours || 0);
  const cannotReschedule = slotLocked || windowLocked;
  const nurseLabel = selected.nurse_name || selected.nurse_name_snapshot || "";
  const nursePhone = String(selected.nurse_phone || selected.nurse_phone_snapshot || "").replace(/\D/g, "");
  const helpTel = config.SUPPORT_PHONE.replace(/\s/g, "");

  const downloadRx = async () => {
    setRxMsg("");
    setRxBusy(true);
    await openAuthedFile(
      `${config.API}/customer/bookings/${selected.id}/prescription`,
      () => setRxMsg("Could not open prescription"),
      authHeaders(),
    );
    setRxBusy(false);
  };

  const attachRx = async (file) => {
    if (!file) return;
    setRxBusy(true);
    setRxMsg("");
    try {
      const fd = new FormData();
      fd.append("prescription", file);
      const { data } = await axios.post(`${config.API}/customer/bookings/${selected.id}/prescription`, fd, { headers: authHeaders() });
      onBookingPatch(selected.id, {
        prescription_file_reference: data.prescription_file_reference,
        prescription_status: "have",
      });
      setRxMsg("Prescription attached");
    } catch (err) {
      setRxMsg(err.response?.data?.detail || "Could not attach prescription");
    } finally {
      setRxBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-end sm:items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-3xl w-full max-w-lg p-6 space-y-4 max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="booking-detail-drawer">
        <div className="flex justify-between items-start">
          <div>
            <h3 className="text-xl font-bold text-[#311b52]" style={{ fontFamily: "Poppins" }}>{selected.service_name}</h3>
            <p className="text-sm text-[#6b7280]">{selected.id}</p>
          </div>
          <button type="button" onClick={onClose} className="text-[#673c96]">Close</button>
        </div>
        <dl className="text-sm space-y-2 text-[#311b52]">
          <div className="flex justify-between"><dt className="text-[#6b7280]">Status</dt><dd>{selected.status}</dd></div>
          <div className="flex justify-between"><dt className="text-[#6b7280]">Beneficiary</dt><dd>{selected.beneficiary_name}</dd></div>
          <div className="flex justify-between"><dt className="text-[#6b7280]">When</dt><dd>{formatDateDMY(selected.preferred_date)} · {selected.preferred_slot}</dd></div>
          {nurseLabel && (
            <div className="flex justify-between"><dt className="text-[#6b7280]">Nurse</dt><dd>{nurseLabel}</dd></div>
          )}
          <div className="flex justify-between"><dt className="text-[#6b7280]">Address</dt><dd className="text-right max-w-[60%]">{selected.address}</dd></div>
          <div className="flex justify-between"><dt className="text-[#6b7280]">Payment</dt><dd>{selected.payment_status} · {selected.payment_method}</dd></div>
          {selected.razorpay_payment_id ? (
            <div className="flex justify-between"><dt className="text-[#6b7280]">Receipt</dt><dd className="font-mono text-[11px]">{selected.razorpay_payment_id}</dd></div>
          ) : null}
        </dl>
        {(paymentSucceeded(selected.payment_status) || selected.status === "completed") && (
          <button type="button"
            onClick={() => openAuthedFile(`${config.API}/customer/bookings/${selected.id}/invoice`, () => setRxMsg("Could not open invoice"), authHeaders(), { allowHtml: true, filenameFallback: "invoice" })}
            className="w-full inline-flex items-center justify-center gap-2 border border-[#673c96]/30 text-[#673c96] rounded-full py-2.5 font-semibold text-sm">
            <Download className="w-4 h-4" /> View invoice / receipt
          </button>
        )}
        {(paymentSucceeded(selected.payment_status) || selected.status === "completed") && (
          <VaccineTrackingPanel bookingId={selected.id} />
        )}

        <div className="border-t pt-4 space-y-2" data-testid="booking-prescription-panel">
          <p className="text-sm font-semibold text-[#311b52]">Prescription</p>
          {hasRx ? (
            <button type="button" onClick={downloadRx} disabled={rxBusy}
              className="w-full inline-flex items-center justify-center gap-2 bg-[#673c96] text-white rounded-full py-2.5 font-semibold text-sm"
              data-testid="download-prescription">
              <Download className="w-4 h-4" /> {rxBusy ? "Opening…" : "View prescription"}
            </button>
          ) : canAttach ? (
            <label className="w-full inline-flex items-center justify-center gap-2 border border-[#673c96]/30 text-[#673c96] rounded-full py-2.5 font-semibold text-sm cursor-pointer">
              <Upload className="w-4 h-4" /> {rxBusy ? "Uploading…" : "Attach prescription"}
              <input type="file" accept=".pdf,image/*,.heic,.heif,.webp" className="hidden" disabled={rxBusy}
                onChange={(e) => attachRx(e.target.files?.[0])} data-testid="attach-prescription" />
            </label>
          ) : (
            <p className="text-sm text-[#6b7280]">No prescription on this booking.</p>
          )}
          {rxMsg && <p className="text-xs text-[#673c96]">{rxMsg}</p>}
        </div>

        {cannotReschedule ? (
          <div className="space-y-3 border-t pt-4" data-testid="booking-helpdesk">
            <p className="text-sm text-[#6b7280]">
              {windowLocked && !slotLocked
                ? `Self-reschedule is only allowed when the appointment is more than ${rescheduleWindowHours} hours away. Please call support.`
                : selected.status === "pending_confirmation"
                ? "This slot is booked. To change it, call the help desk — you cannot reschedule it yourself."
                : "This slot is confirmed. To change it, call the help desk — you cannot reschedule it yourself."}
            </p>
            {nurseLabel ? (
              <p className="text-sm text-[#311b52]">Nurse: <strong>{nurseLabel}</strong></p>
            ) : (
              <p className="text-sm text-[#6b7280]">Nurse will be assigned shortly.</p>
            )}
            <div className="grid grid-cols-2 gap-2">
              {nursePhone && (
                <a href={`tel:${nursePhone.length === 10 ? "+91" : "+"}${nursePhone.replace(/^\+/, "")}`} className="inline-flex items-center justify-center gap-2 bg-[#311b52] text-white rounded-full py-2.5 font-semibold text-sm">Call nurse</a>
              )}
              <a href={`tel:${helpTel}`} className="inline-flex items-center justify-center gap-2 bg-[#f37721] text-white rounded-full py-2.5 font-semibold text-sm col-span-2 sm:col-span-1">Call now</a>
            </div>
            <a href={`tel:${helpTel}`} className="block text-center text-sm font-semibold text-[#673c96]">Help desk · {config.SUPPORT_PHONE}</a>
            <a className="block text-center text-sm font-semibold text-[#673c96]" href={`https://wa.me/${config.WHATSAPP_NUMBER}?text=${encodeURIComponent(`Hi, help with booking ${selected.id}`)}`}>WhatsApp help desk</a>
            {["awaiting_payment", "pending_confirmation", "confirmed"].includes(selected.status) && (
              <button type="button" onClick={() => onCancel(selected.id)} className="w-full border border-[#ee4036] text-[#ee4036] rounded-full py-2.5 font-semibold">Cancel booking</button>
            )}
          </div>
        ) : (
          <div className="space-y-3 border-t pt-4">
            <p className="text-sm font-semibold text-[#311b52]">Reschedule</p>
            <BookingSlotCalendar
              days={calDays}
              selected={reschedule.date}
              loading={calLoading}
              totalSlots={calTotal}
              onSelect={(iso) => setReschedule({ date: iso, slot: "" })}
            />
            {reschedule.date && (
              <div className="grid sm:grid-cols-2 gap-2 max-h-48 overflow-auto">
                {hoursLoading && <p className="text-sm text-[#6b7280] col-span-2">Loading hours…</p>}
                {hours.map((s) => {
                  const on = reschedule.slot === s.hour;
                  return (
                    <button key={s.hour} type="button" onClick={() => setReschedule({ ...reschedule, slot: s.hour })}
                      className={`text-left rounded-xl border px-3 py-2 text-sm ${on ? "border-[#f37721] bg-[#f37721]/5" : "border-[#e5e7eb]"}`}>
                      {s.label}
                    </button>
                  );
                })}
              </div>
            )}
            <button type="button" disabled={!reschedule.date || !reschedule.slot} onClick={() => onReschedule(selected.id)} className="w-full bg-[#673c96] text-white rounded-full py-2.5 font-semibold disabled:opacity-50">Save new window</button>
            <button type="button" onClick={() => onCancel(selected.id)} className="w-full border border-[#ee4036] text-[#ee4036] rounded-full py-2.5 font-semibold">Cancel booking</button>
          </div>
        )}
      </div>
    </div>
  );
}

export function CustomerDashboard() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = searchParams.get("tab") || "overview";
  const active = ["bookings", "settings", "vaccines"].includes(tab) ? tab : "overview";

  const [bookings, setBookings] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [wallet, setWallet] = useState(null);
  const [user, setUser] = useState(readUser());
  const [listTab, setListTab] = useState("upcoming");
  const [selected, setSelected] = useState(null);
  const [error, setError] = useState("");
  const [reschedule, setReschedule] = useState({ date: "", slot: "morning" });
  const [vaccineRecords, setVaccineRecords] = useState([]);
  const [rescheduleWindowHours, setRescheduleWindowHours] = useState(24);
  const [downloadingId, setDownloadingId] = useState("");

  const load = async () => {
    try {
      const me = await axios.get(`${config.API}/customer/me`, { headers: authHeaders() });
      setUser(me.data);
      localStorage.setItem("customer_user", JSON.stringify(me.data));
      notifyAuthChanged();
      const [b, w, n, rec, rules] = await Promise.all([
        axios.get(`${config.API}/customer/bookings`, { headers: authHeaders() }),
        axios.get(`${config.API}/customer/wallet`, { headers: authHeaders() }).catch(() => ({ data: null })),
        axios.get(`${config.API}/customer/notifications`, { headers: authHeaders() }).catch(() => ({ data: { items: [] } })),
        axios.get(`${config.API}/customer/vaccine-records`, { headers: authHeaders() }).catch(() => ({ data: { items: [] } })),
        axios.get(`${config.API}/config/booking-rules`).catch(() => ({ data: {} })),
      ]);
      setBookings(b.data.items || []);
      setWallet(w.data);
      setNotifications(n.data.items || []);
      setVaccineRecords(rec.data.items || []);
      setRescheduleWindowHours(Number(rules.data.reschedule_allowed_window) || 24);
    } catch (err) {
      if (err?.response?.status === 401) {
        localStorage.removeItem("customer_token");
        notifyAuthChanged();
        navigate("/login");
      } else {
        setError("Could not load your account. Is the API running on 127.0.0.1:8000?");
      }
    }
  };

  useEffect(() => {
    if (!requireCustomerAuth(navigate)) return;
    load();
  }, [navigate]);

  const upcoming = bookings.filter((b) => !["completed", "cancelled"].includes(b.status) && paymentSucceeded(b.payment_status));
  const past = bookings.filter((b) => ["completed", "cancelled"].includes(b.status));
  const completed = bookings.filter((b) => b.status === "completed");
  const totalSpent = bookings
    .filter((b) => paymentSucceeded(b.payment_status) || b.status === "completed")
    .reduce((sum, b) => sum + (Number(b.amount || b.indicative_price || b.final_amount) || 0), 0);

  const cancelBooking = async (id) => {
    if (!window.confirm('Are you sure you want to cancel this booking? This action cannot be undone.')) return;
    await axios.post(`${config.API}/customer/bookings/${id}/cancel`, {}, { headers: authHeaders() });
    load();
    setSelected(null);
  };

  const saveReschedule = async (id) => {
    await axios.post(`${config.API}/customer/bookings/${id}/reschedule`, {
      preferred_date: reschedule.date,
      preferred_slot: reschedule.slot,
    }, { headers: authHeaders() });
    load();
    setSelected(null);
  };

  const openBooking = (b) => {
    setSelected(b);
    setReschedule({ date: b.preferred_date || "", slot: b.preferred_slot || "morning" });
  };

  return (
    <CustomerShell active={active}>
      {error && <p className="text-sm text-[#ee4036] mb-4">{error}</p>}

      {active === "overview" && (
        <div className="space-y-6" data-testid="dashboard-overview">
          <div className="grid sm:grid-cols-2 xl:grid-cols-4 gap-4">
            <StatCard label="Total Bookings" value={bookings.length} icon={CalendarDays} iconBg="bg-[#673c96]/12" iconColor="text-[#673c96]" />
            <StatCard label="Upcoming" value={upcoming.length} icon={Clock} iconBg="bg-emerald-50" iconColor="text-emerald-600" />
            <StatCard label="Completed" value={completed.length} icon={Syringe} iconBg="bg-[#f37721]/12" iconColor="text-[#f37721]" />
            <StatCard label="Total Spent" value={`₹${totalSpent || wallet?.spent || 0}`} icon={IndianRupee} iconBg="bg-teal-50" iconColor="text-teal-600" />
          </div>

          {notifications.length > 0 && (
            <div className="bg-white rounded-2xl shadow-sm border border-black/[0.03] p-5">
              <div className="flex items-center gap-2 mb-3">
                <Bell className="w-4 h-4 text-[#673c96]" />
                <h2 className="font-semibold text-[#311b52]" style={{ fontFamily: "Poppins" }}>Updates</h2>
              </div>
              <ul className="space-y-2 text-sm text-[#6b7280]">
                {notifications.slice(0, 5).map((n) => (
                  <li key={n.id} className="flex justify-between gap-2">
                    <span>{n.title || n.body}</span>
                    {!n.read && <span className="text-[10px] uppercase text-[#f37721]">New</span>}
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="bg-white rounded-2xl shadow-sm border border-black/[0.03] p-6 md:p-8 min-h-[280px]">
            <h2 className="text-lg font-semibold text-[#1a1a2e]" style={{ fontFamily: "Poppins" }}>Upcoming Bookings</h2>
            {upcoming.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-14 text-center">
                <Syringe className="w-16 h-16 text-[#d1d5db] mb-4" strokeWidth={1.25} />
                <p className="text-[#6b7280] mb-2" style={{ fontFamily: "Montserrat" }}>No upcoming bookings</p>
                <Link to="/vaccination-catalogue" className="text-[#673c96] font-semibold hover:text-[#f37721] transition-colors" style={{ fontFamily: "Montserrat" }}>
                  Book now
                </Link>
              </div>
            ) : (
              <div className="mt-4 space-y-3">
                {upcoming.slice(0, 5).map((b) => (
                  <button
                    key={b.id}
                    type="button"
                    onClick={() => openBooking(b)}
                    className="w-full text-left rounded-xl border border-[#e5e7eb] p-4 hover:border-[#673c96]/30 hover:shadow-sm transition-all"
                  >
                    <div className="flex justify-between gap-2">
                      <h3 className="font-semibold text-[#311b52]">{b.service_name || b.vaccine_name}</h3>
                      <span className={`text-xs px-2 py-1 rounded-full shrink-0 ${STATUS_STYLE[b.status] || "bg-slate-100"}`}>{b.status}</span>
                    </div>
                    <p className="text-sm text-[#6b7280] mt-1">{b.beneficiary_name} · {formatDateDMY(b.preferred_date)} · {b.preferred_slot}</p>
                    {(b.nurse_name || b.nurse_name_snapshot) && (
                      <p className="text-xs text-[#311b52] mt-1">Nurse: {b.nurse_name || b.nurse_name_snapshot}</p>
                    )}
                    {b.prescription_file_reference
                      ? <p className="text-xs text-[#673c96] mt-1 font-semibold">Prescription ready — tap to download</p>
                      : <p className="text-xs text-[#f37721] mt-1">No prescription yet — tap to attach</p>}
                  </button>
                ))}
                {upcoming.length > 5 && (
                  <button type="button" onClick={() => setSearchParams({ tab: "bookings" })} className="text-sm text-[#673c96] font-semibold">
                    View all bookings →
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {active === "bookings" && (
        <div className="space-y-4" data-testid="dashboard-bookings">
          <div className="flex flex-wrap gap-2">
            {[
              { id: "upcoming", label: `Upcoming (${upcoming.length})` },
              { id: "past", label: `Past (${past.length})` },
            ].map((t) => (
              <button
                key={t.id}
                type="button"
                onClick={() => setListTab(t.id)}
                className={`px-4 py-2 rounded-full text-sm font-semibold transition-colors ${
                  listTab === t.id ? "bg-[#673c96] text-white" : "bg-white text-[#6b7280] border border-[#e5e7eb]"
                }`}
                style={{ fontFamily: "Montserrat" }}
              >
                {t.label}
              </button>
            ))}
            <Link to="/vaccination-catalogue" className="ml-auto px-4 py-2 rounded-full text-sm font-semibold bg-[#f37721] text-white" style={{ fontFamily: "Montserrat" }}>
              + Book now
            </Link>
          </div>

          {listTab === "upcoming" || listTab === "past" ? (
            <>
              {(listTab === "upcoming" ? upcoming : past).length === 0 && (
                <div className="bg-white rounded-2xl shadow-sm p-12 text-center">
                  <Syringe className="w-14 h-14 text-[#d1d5db] mx-auto mb-3" strokeWidth={1.25} />
                  <p className="text-[#6b7280]">No {listTab} bookings yet.</p>
                  <Link to="/vaccination-catalogue" className="inline-block mt-3 text-[#673c96] font-semibold">Book now</Link>
                </div>
              )}
              <div className="space-y-3">
                {(listTab === "upcoming" ? upcoming : past).map((b) => (
                  <button
                    key={b.id}
                    type="button"
                    onClick={() => openBooking(b)}
                    className="w-full text-left bg-white rounded-2xl p-5 shadow-sm border border-black/[0.03] hover:shadow-md transition-shadow"
                  >
                    <div className="flex justify-between gap-2">
                      <h2 className="font-semibold text-[#311b52]">{b.service_name || b.vaccine_name}</h2>
                      <span className={`text-xs px-2 py-1 rounded-full shrink-0 ${STATUS_STYLE[b.status] || "bg-slate-100"}`}>{b.status}</span>
                    </div>
                    <p className="text-sm text-[#6b7280] mt-1">{b.beneficiary_name} · {formatDateDMY(b.preferred_date)} · {b.preferred_slot}</p>
                    {(b.nurse_name || b.nurse_name_snapshot) && (
                      <p className="text-xs text-[#311b52] mt-1">Nurse: {b.nurse_name || b.nurse_name_snapshot}</p>
                    )}
                    {b.prescription_file_reference
                      ? <p className="text-xs text-[#673c96] mt-1 font-semibold">Prescription ready — tap to download</p>
                      : !["cancelled", "completed"].includes(b.status)
                        ? <p className="text-xs text-[#f37721] mt-1">No prescription yet — tap to attach</p>
                        : null}
                    <p className="text-xs text-[#9ca3af] mt-1">Ref: {b.id} · Payment: {b.payment_status || "pending"}</p>
                  </button>
                ))}
              </div>
            </>
          ) : null}
        </div>
      )}

      {active === "vaccines" && (
        <div className="space-y-4" data-testid="dashboard-vaccines">
          <div>
            <h2 className="text-lg font-semibold text-[#311b52]" style={{ fontFamily: "Poppins" }}>My vaccines</h2>
            <p className="text-sm text-[#6b7280] mt-1">Every vaccine that has been applied for you or your family, with visit details.</p>
          </div>
          {vaccineRecords.length === 0 ? (
            <div className="bg-white rounded-2xl shadow-sm p-12 text-center">
              <Syringe className="w-14 h-14 text-[#d1d5db] mx-auto mb-3" strokeWidth={1.25} />
              <p className="text-[#6b7280]">No applied vaccines yet. Completed visits will appear here.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {vaccineRecords.map((r) => (
                <div key={r.id || r.booking_id} className="bg-white rounded-2xl p-5 shadow-sm border border-black/[0.03]" data-testid={`vaccine-record-${r.id}`}>
                  <div className="flex justify-between gap-2">
                    <h3 className="font-semibold text-[#311b52]">{r.vaccine_name}</h3>
                    <span className="text-xs px-2 py-1 rounded-full bg-emerald-100 text-emerald-800 shrink-0">Applied</span>
                  </div>
                  <p className="text-sm text-[#6b7280] mt-1">{r.beneficiary_name}</p>
                  <dl className="mt-3 grid sm:grid-cols-2 gap-2 text-sm">
                    <div><dt className="text-[#9ca3af] text-xs">Date & time of administration</dt><dd>{r.applied_at ? formatDateTimeDMY(r.applied_at) : "—"}</dd></div>
                    <div><dt className="text-[#9ca3af] text-xs">Visit slot</dt><dd>{formatDateDMY(r.preferred_date)} {r.preferred_slot || ""}</dd></div>
                    <div><dt className="text-[#9ca3af] text-xs">Nurse</dt><dd>{r.nurse_name || "—"}</dd></div>
                    <div><dt className="text-[#9ca3af] text-xs">Batch</dt><dd>{r.batch_number || "—"}</dd></div>
                    <div className="sm:col-span-2"><dt className="text-[#9ca3af] text-xs">Address</dt><dd>{r.address || "—"}</dd></div>
                    <div className="sm:col-span-2"><dt className="text-[#9ca3af] text-xs">Booking</dt><dd className="font-mono text-xs">{r.booking_id}</dd></div>
                    {(r.cold_chain && (r.cold_chain.last_temp != null || r.cold_chain.maintained != null)) && (
                      <div className="sm:col-span-2">
                        <dt className="text-[#9ca3af] text-xs mb-1">Cold chain</dt>
                        <dd>
                          <IceboxTempBar temp={r.cold_chain.last_temp} compact live={false} explain />
                          {r.cold_chain.min_temp != null && r.cold_chain.max_temp != null ? (
                            <p className="text-xs text-[#6b7280] mt-1">Trip range {r.cold_chain.min_temp}–{r.cold_chain.max_temp}°C</p>
                          ) : null}
                        </dd>
                      </div>
                    )}
                  </dl>
                  <button
                    type="button"
                    disabled={downloadingId === (r.id || r.booking_id)}
                    onClick={async () => {
                      const id = r.id || r.booking_id;
                      setDownloadingId(id);
                      try {
                        await openVaccineReport(r, user);
                      } finally {
                        setDownloadingId("");
                      }
                    }}
                    className="mt-4 w-full sm:w-auto inline-flex items-center justify-center gap-2 border-2 border-[#673c96] text-[#673c96] rounded-full px-4 py-2.5 text-sm font-semibold disabled:opacity-60"
                    data-testid={`vaccine-report-${r.id}`}
                  >
                    <Download className="w-4 h-4" />
                    {downloadingId === (r.id || r.booking_id) ? "Preparing…" : "Download report"}
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {active === "settings" && (
        <div className="bg-white rounded-2xl shadow-sm border border-black/[0.03] p-6 space-y-6" data-testid="dashboard-settings">
          <div>
            <h2 className="text-lg font-semibold text-[#311b52]" style={{ fontFamily: "Poppins" }}>Settings</h2>
            <p className="text-sm text-[#6b7280] mt-1" style={{ fontFamily: "Montserrat" }}>
              Account preferences and privacy controls.
            </p>
            {user.phone_only && (
              <p className="mt-3 text-sm text-[#673c96] bg-[#fdfcff] border border-[#673c96]/15 rounded-xl px-4 py-3">
                You signed in with mobile OTP. Add an email and password on Profile if you also want email login.
              </p>
            )}
          </div>
          <div className="space-y-3 text-sm" style={{ fontFamily: "Montserrat" }}>
            <div className="flex justify-between items-center py-3 border-b border-[#f3f4f6]">
              <span className="text-[#311b52]">Email</span>
              <span className="text-[#6b7280]">{formatDisplayEmail(user) || "Not added yet"}</span>
            </div>
            <div className="flex justify-between items-center py-3 border-b border-[#f3f4f6]">
              <span className="text-[#311b52]">Phone</span>
              <span className="text-[#6b7280]">{user.phone ? formatDisplayName({ phone: user.phone }) : "—"}</span>
            </div>

          </div>
          <div className="flex flex-col sm:flex-row gap-3 pt-2">
            <Link to="/profile" className="px-5 py-2.5 rounded-full bg-[#673c96] text-white text-sm font-semibold text-center" style={{ fontFamily: "Montserrat" }}>
              Edit profile & family
            </Link>
            <Link to="/privacy-policy" className="px-5 py-2.5 rounded-full border border-[#e5e7eb] text-[#311b52] text-sm font-semibold text-center" style={{ fontFamily: "Montserrat" }}>
              Privacy policy
            </Link>
          </div>
        </div>
      )}

      <BookingDetailDrawer
        selected={selected}
        reschedule={reschedule}
        setReschedule={setReschedule}
        rescheduleWindowHours={rescheduleWindowHours}
        onClose={() => setSelected(null)}
        onCancel={cancelBooking}
        onReschedule={saveReschedule}
        onBookingPatch={(id, partial) => {
          setSelected((s) => (s && s.id === id ? { ...s, ...partial } : s));
          setBookings((list) => list.map((b) => (b.id === id ? { ...b, ...partial } : b)));
        }}
      />
    </CustomerShell>
  );
}

export function ProfilePage() {
  const navigate = useNavigate();
  const [user, setUser] = useState(readUser());
  const [fullName, setFullName] = useState(user.full_name || "");
  const [phone, setPhone] = useState(user.phone ? String(user.phone).slice(-10) : "");
  const [address, setAddress] = useState(user.address || "");
  const [family, setFamily] = useState([]);
  const [addresses, setAddresses] = useState([]);
  const [famForm, setFamForm] = useState({ full_name: "", relation: "Self", date_of_birth: "" });
  const [addrForm, setAddrForm] = useState({ label: "Home", line1: "", city: "Gurgaon", pincode: "" });
  const [exportData, setExportData] = useState(null);
  const [msg, setMsg] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [dob, setDob] = useState(user.date_of_birth || "");
  const [section, setSection] = useState("profile");
  const [deleting, setDeleting] = useState(false);
  const [photoUrl, setPhotoUrl] = useState("");
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [servicePins, setServicePins] = useState([]);
  const [pinsLoaded, setPinsLoaded] = useState(false);

  const loadExtra = async () => {
    const [f, a] = await Promise.all([
      axios.get(`${config.API}/customer/family`, { headers: authHeaders() }),
      axios.get(`${config.API}/customer/addresses`, { headers: authHeaders() }),
    ]);
    setFamily(f.data.items || []);
    setAddresses(a.data.items || []);
  };

  useEffect(() => {
    if (!requireCustomerAuth(navigate)) return;
    axios.get(`${config.API}/customer/me`, { headers: authHeaders() })
      .then(({ data }) => {
        setUser(data);
        setFullName(data.full_name || "");
        setPhone(data.phone ? String(data.phone).slice(-10) : "");
        setAddress(data.address || "");
        setDob(data.date_of_birth || "");
        localStorage.setItem("customer_user", JSON.stringify(data));
        notifyAuthChanged();
        if (data.has_photo) {
          axios.get(`${config.API}/customer/profile/photo`, { headers: authHeaders(), responseType: "blob" })
            .then(({ data: blob }) => setPhotoUrl((prev) => {
              if (prev) URL.revokeObjectURL(prev);
              return URL.createObjectURL(blob);
            }))
            .catch(() => setPhotoUrl(""));
        }
      })
      .catch(() => navigate("/login"));
    loadExtra().catch(() => {});
    axios.get(`${config.API}/config/service-pincodes`)
      .then(({ data }) => setServicePins(Array.isArray(data.pincodes) ? data.pincodes : []))
      .catch(() => setServicePins([]))
      .finally(() => setPinsLoaded(true));
  }, [navigate]);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true); setError(""); setMsg("");
    try {
      const err = fieldError({
        name: fullName,
        phone: phone || undefined,
        email: email || undefined,
        password: password || undefined,
      });
      if (err) { setError(err); return; }
      const payload = { full_name: fullName, phone: phone ? indianMobile10(phone) : undefined, address, date_of_birth: dob };
      if (email.trim()) payload.email = email.trim();
      if (password.trim()) payload.password = password;
      const { data } = await axios.patch(`${config.API}/customer/profile`, payload, { headers: authHeaders() });
      setUser(data);
      localStorage.setItem("customer_user", JSON.stringify(data));
      notifyAuthChanged();
      setMsg("Profile saved");
    } catch (err) {
      setError(err.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const addFamily = async (e) => {
    e.preventDefault();
    const err = fieldError({ name: famForm.full_name });
    if (err) { setError(err); return; }
    if (!famForm.date_of_birth) { setError("Date of birth is required"); return; }
    await axios.post(`${config.API}/customer/family`, famForm, { headers: authHeaders() });
    setFamForm({ full_name: "", relation: "Self", date_of_birth: "" });
    loadExtra();
  };

  const addAddress = async (e) => {
    e.preventDefault();
    setError("");
    const pin = normalizePincode(addrForm.pincode);
    const pinErr = pinServiceError(pin, servicePins, { listReady: pinsLoaded, requireComplete: true });
    if (pinErr) { setError(pinErr); return; }
    try {
      await axios.post(`${config.API}/customer/addresses`, { ...addrForm, pincode: pin }, { headers: authHeaders() });
      setAddrForm({ label: "Home", line1: "", city: "Gurgaon", pincode: "" });
      loadExtra();
    } catch (err) {
      const d = err?.response?.data?.detail;
      setError(typeof d === "string" ? d : "Could not save address");
    }
  };

  return (
    <CustomerShell active="profile">
      <div className="flex flex-wrap gap-2 mb-6">
        {["profile", "family", "addresses", "privacy"].map((s) => (
          <button key={s} type="button" onClick={() => setSection(s)}
            className={`px-4 py-2 rounded-full text-sm font-semibold capitalize transition-colors ${
              section === s ? "bg-[#673c96] text-white" : "bg-white text-[#6b7280] border border-[#e5e7eb]"
            }`}
            style={{ fontFamily: "Montserrat" }}
          >
            {s}
          </button>
        ))}
      </div>

      {section === "profile" && (
        <form onSubmit={save} className="space-y-3 bg-white rounded-2xl shadow-sm border border-black/[0.03] p-6 max-w-xl">
          <p className="text-sm text-[#6b7280]">{formatDisplayEmail(user) || (user.phone ? `Phone · ${formatDisplayName({ phone: user.phone })}` : "Add your details below")}</p>
          <div className="flex items-center gap-4">
            <div className="w-20 h-20 rounded-full overflow-hidden bg-[#f5f3f7] border border-[#673c96]/15 flex items-center justify-center">
              {photoUrl ? (
                <img src={photoUrl} alt="Profile" className="w-full h-full object-cover" />
              ) : (
                <Camera className="w-7 h-7 text-[#673c96]/40" />
              )}
            </div>
            <label className="text-sm font-semibold text-[#673c96] cursor-pointer">
              {uploadingPhoto ? "Uploading…" : "Upload profile photo"}
              <input type="file" accept="image/*" className="hidden" disabled={uploadingPhoto} onChange={async (e) => {
                const file = e.target.files?.[0];
                e.target.value = "";
                if (!file) return;
                setUploadingPhoto(true); setError(""); setMsg("");
                try {
                  const fd = new FormData();
                  fd.append("photo", file);
                  const { data } = await axios.post(`${config.API}/customer/profile/photo`, fd, { headers: authHeaders() });
                  setUser(data);
                  localStorage.setItem("customer_user", JSON.stringify(data));
                  notifyAuthChanged();
                  const blob = await axios.get(`${config.API}/customer/profile/photo`, { headers: authHeaders(), responseType: "blob" });
                  setPhotoUrl((prev) => {
                    if (prev) URL.revokeObjectURL(prev);
                    return URL.createObjectURL(blob.data);
                  });
                  setMsg("Profile photo updated");
                } catch (err) {
                  setError(err.response?.data?.detail || "Could not upload photo");
                } finally {
                  setUploadingPhoto(false);
                }
              }} />
            </label>
          </div>
          <input value={fullName} onChange={(e) => setFullName(e.target.value)} placeholder="Full name" className="w-full rounded-xl border border-[#e5e7eb] px-4 py-3" data-testid="profile-name" />
          <input value={phone} onChange={(e) => setPhone(e.target.value.replace(/\D/g, '').slice(0, 10))} maxLength={10} placeholder="Mobile" className="w-full rounded-xl border border-[#e5e7eb] px-4 py-3" data-testid="profile-phone" />
          <div>
            <p className="text-xs text-[#6b7280] mb-1">Date of birth *</p>
            <DmyDateInput required noFuture value={dob} onChange={setDob} className="w-full rounded-xl border border-[#e5e7eb] px-4 py-3" />
            {ageFromDob(dob) !== "" && <p className="text-[11px] text-[#6b7280] mt-1">Age: {ageFromDob(dob)} years</p>}
          </div>
          <textarea value={address} onChange={(e) => setAddress(e.target.value)} placeholder="Address" rows={2} className="w-full rounded-xl border border-[#e5e7eb] px-4 py-3" />
          {user.phone_only && (
            <div className="rounded-2xl border border-[#673c96]/15 p-4 space-y-3 bg-[#fdfcff]">
              <p className="text-sm font-semibold text-[#311b52]">Add email & password (optional)</p>
              <p className="text-xs text-[#6b7280]">You can keep using mobile OTP. Email lets you also sign in with a password.</p>
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email" className="w-full rounded-xl border border-[#e5e7eb] px-4 py-3" data-testid="profile-email" />
              <input type="password" minLength={8} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Password (min 8 characters)" className="w-full rounded-xl border border-[#e5e7eb] px-4 py-3" data-testid="profile-password" />
            </div>
          )}
          <button disabled={saving} className="w-full bg-[#f37721] text-white rounded-full py-3 font-semibold">{saving ? "Saving…" : "Save profile"}</button>
          {msg && <p className="text-sm text-emerald-700">{msg}</p>}
          {error && <p className="text-sm text-[#ee4036]">{error}</p>}
        </form>
      )}

      {section === "family" && (
        <div className="space-y-4 max-w-xl">
          {family.map((m) => (
            <div key={m.id} className="bg-white rounded-2xl shadow-sm border border-black/[0.03] p-4 flex justify-between items-center">
              <div>
                <p className="font-semibold text-[#311b52]">{m.full_name}</p>
                <p className="text-xs text-[#6b7280]">{m.relation}{m.date_of_birth ? ` · ${formatDateDMY(m.date_of_birth)}${ageFromDob(m.date_of_birth) !== "" ? ` · ${ageFromDob(m.date_of_birth)} yrs` : ""}` : ""}</p>
              </div>
              <button type="button" className="text-sm text-[#ee4036]" onClick={async () => { await axios.delete(`${config.API}/customer/family/${m.id}`, { headers: authHeaders() }); loadExtra(); }}>Remove</button>
            </div>
          ))}
          <form onSubmit={addFamily} className="bg-white rounded-2xl shadow-sm border border-black/[0.03] p-5 space-y-3">
            <h3 className="font-semibold text-[#311b52]" style={{ fontFamily: "Poppins" }}>Add family member</h3>
            <input required value={famForm.full_name} onChange={(e) => setFamForm({ ...famForm, full_name: e.target.value })} placeholder="Full name" className="w-full border border-[#e5e7eb] rounded-xl px-4 py-3" />
            <select value={famForm.relation} onChange={(e) => setFamForm({ ...famForm, relation: e.target.value })} className="w-full border border-[#e5e7eb] rounded-xl px-4 py-3">
              {FAMILY_RELATIONS.map((r) => <option key={r}>{r}</option>)}
            </select>
            <div>
              <p className="text-xs text-[#6b7280] mb-1">Date of birth *</p>
              <DmyDateInput required noFuture value={famForm.date_of_birth} onChange={(iso) => setFamForm({ ...famForm, date_of_birth: iso })} className="w-full border border-[#e5e7eb] rounded-xl px-4 py-3" />
              {ageFromDob(famForm.date_of_birth) !== "" && <p className="text-[11px] text-[#6b7280] mt-1">Age: {ageFromDob(famForm.date_of_birth)} years</p>}
            </div>
            <button className="w-full bg-[#673c96] text-white rounded-full py-3 font-semibold">Add member</button>
            {error && <p className="text-sm text-[#ee4036]">{error}</p>}
          </form>
        </div>
      )}

      {section === "addresses" && (
        <div className="space-y-4 max-w-xl">
          {addresses.map((a) => (
            <div key={a.id} className="bg-white rounded-2xl shadow-sm border border-black/[0.03] p-4 flex justify-between gap-2">
              <div>
                <p className="font-semibold text-[#311b52]">{a.label}</p>
                <p className="text-sm text-[#6b7280]">{a.line1}, {a.city} {a.pincode}</p>
              </div>
              <button type="button" className="text-sm text-[#ee4036]" onClick={async () => { await axios.delete(`${config.API}/customer/addresses/${a.id}`, { headers: authHeaders() }); loadExtra(); }}>Remove</button>
            </div>
          ))}
          <form onSubmit={addAddress} className="bg-white rounded-2xl shadow-sm border border-black/[0.03] p-5 space-y-3">
            <h3 className="font-semibold text-[#311b52]" style={{ fontFamily: "Poppins" }}>Add address</h3>
            <input value={addrForm.label} onChange={(e) => setAddrForm({ ...addrForm, label: e.target.value })} placeholder="Label (Home / Office)" className="w-full border border-[#e5e7eb] rounded-xl px-4 py-3" />
            <input required value={addrForm.line1} onChange={(e) => setAddrForm({ ...addrForm, line1: e.target.value })} placeholder="Address line" className="w-full border border-[#e5e7eb] rounded-xl px-4 py-3" />
            <div className="flex gap-2">
              <input value={addrForm.city} onChange={(e) => setAddrForm({ ...addrForm, city: e.target.value })} placeholder="City" className="flex-1 border border-[#e5e7eb] rounded-xl px-4 py-3" />
              <input required value={addrForm.pincode} onChange={(e) => setAddrForm({ ...addrForm, pincode: e.target.value.replace(/\D/g, "").slice(0, 6) })} placeholder="PIN *" maxLength={6} inputMode="numeric" className={`w-28 border rounded-xl px-4 py-3 ${
                normalizePincode(addrForm.pincode).length === 6 && pinServiceError(addrForm.pincode, servicePins, { listReady: pinsLoaded })
                  ? "border-red-500 bg-red-50"
                  : "border-[#e5e7eb]"
              }`} />
            </div>
            {normalizePincode(addrForm.pincode).length === 6 && pinServiceError(addrForm.pincode, servicePins, { listReady: pinsLoaded }) ? (
              <p className="text-sm text-[#ee4036]">{pinServiceError(addrForm.pincode, servicePins, { listReady: pinsLoaded })}</p>
            ) : null}
            {error && <p className="text-sm text-[#ee4036]">{error}</p>}
            <button className="w-full bg-[#673c96] text-white rounded-full py-3 font-semibold">Save address</button>
          </form>
        </div>
      )}

      {section === "privacy" && (
        <div className="space-y-4 max-w-xl">
          <div className="bg-white rounded-2xl shadow-sm border border-black/[0.03] p-5 space-y-3">
            <h3 className="font-semibold text-[#311b52]" style={{ fontFamily: "Poppins" }}>Download your data</h3>
            <p className="text-sm text-[#6b7280]">Get a copy of your bookings, family members, and addresses before you change or delete anything.</p>
            <button type="button" onClick={async () => {
              setError("");
              try {
                const { data } = await axios.get(`${config.API}/dpdp/export`, { headers: authHeaders() });
                setExportData(data);
                const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
                const url = URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url;
                a.download = "bookmyjab-my-data.json";
                a.click();
                URL.revokeObjectURL(url);
              } catch (err) {
                setError(err.response?.data?.detail || "Could not download data");
              }
            }} className="w-full bg-[#673c96] text-white rounded-full py-3 font-semibold">Download my data</button>
            {exportData && <p className="text-xs text-emerald-700">Download started. A copy is also shown below.</p>}
            {exportData && <pre className="text-xs bg-[#fafafa] p-4 rounded-xl overflow-auto max-h-64">{JSON.stringify(exportData, null, 2)}</pre>}
          </div>
          <details className="rounded-2xl border border-[#e5e7eb] bg-[#fafafa] p-4">
            <summary className="text-xs text-[#9ca3af] cursor-pointer">Delete account</summary>
            <p className="text-xs text-[#6b7280] mt-3">This signs you out and closes the account. Past bookings and vaccine records stay in the admin portal.</p>
            <button type="button" disabled={deleting} onClick={async () => {
              if (!window.confirm("Are you sure you want to delete your account? This cannot be undone.")) return;
              if (!window.confirm("Please confirm again: delete this account? Past bookings will remain with BookMyJab operations.")) return;
              setDeleting(true);
              try {
                await axios.post(`${config.API}/dpdp/delete-account`, { confirmation: "DELETE" }, { headers: authHeaders() });
                localStorage.clear();
                notifyAuthChanged();
                navigate("/");
              } catch (err) {
                setError(err.response?.data?.detail || "Could not delete account");
              } finally {
                setDeleting(false);
              }
            }} className="mt-3 text-xs text-[#9ca3af] underline">{deleting ? "Deleting…" : "Permanently delete my account"}</button>
          </details>
        </div>
      )}

      {msg && <p className="mt-4 text-sm text-[#673c96]">{msg}</p>}
      {error && <p className="mt-2 text-sm text-[#ee4036]">{error}</p>}
    </CustomerShell>
  );
}
